// Ключи агента для MCP-доступа (005: FR-030 – FR-032; ADR-0025, research, Р12). Выдаёт и отзывает
// администратор клиента для пользователя; ключ показывается один раз и хранится хешем.
import { z } from '../kernel/zod.ts';
import { DateSchema, IdSchema, PersonRefSchema, TimestampSchema } from '../kernel/common.ts';
import { fieldMessages } from '../kernel/field-messages.ts';

/** Начало ключа: kpa_ и 32 случайных байта в base32. */
export const AGENT_KEY_PREFIX = 'kpa_';
export const AGENT_KEY_PATTERN = /^kpa_[a-z2-7]{52}$/;
/** Срок ключа — не больше 90 дней от выдачи. */
export const AGENT_KEY_MAX_DAYS = 90;
/** Вызовов ключа в минуту; больше — KEY-002. */
export const AGENT_KEY_CALLS_PER_MINUTE = 60;

export const agentKeyStates = ['active', 'expired', 'revoked'] as const;
export type AgentKeyState = (typeof agentKeyStates)[number];
export const AgentKeyStateSchema = z.enum(agentKeyStates);

export const agentKeyStateLabels = {
  active: 'Действует',
  expired: 'Истёк',
  revoked: 'Отозван',
} as const satisfies Record<AgentKeyState, string>;

export const AgentKeySchema = z.object({
  id: IdSchema,
  name: z.string(),
  /** Пользователь, от имени которого действует агент: права — его на момент вызова. */
  user: PersonRefSchema,
  /** Последний день действия. */
  expiresOn: DateSchema,
  state: AgentKeyStateSchema,
  issuedBy: PersonRefSchema,
  issuedAt: TimestampSchema,
  revokedAt: TimestampSchema.nullable(),
  lastUsedAt: TimestampSchema.nullable(),
  calls: z.number().int().min(0),
  denied: z.number().int().min(0),
});
export type AgentKey = z.infer<typeof AgentKeySchema>;

export const AgentKeyListSchema = z.object({ items: z.array(AgentKeySchema) });

/** Срок дальше AGENT_KEY_MAX_DAYS сервер отклоняет ошибкой поля expiresOn (REC-007). */
export const IssueAgentKeyBodySchema = z.object({
  name: z
    .string({ error: fieldMessages.agentKeyName })
    .trim()
    .min(2, { error: fieldMessages.agentKeyName })
    .max(100, { error: fieldMessages.agentKeyName }),
  userId: IdSchema,
  expiresOn: DateSchema,
});

/** Ответ выдачи: ключ целиком — только здесь, один раз. */
export const IssuedAgentKeySchema = z.object({
  key: AgentKeySchema,
  secret: z.string().regex(AGENT_KEY_PATTERN),
});
export type IssuedAgentKey = z.infer<typeof IssuedAgentKeySchema>;
